import { useLayoutEffect, useRef } from 'react';

import Typography from '@mui/material/Typography';

interface Props {
  readonly description: string;
  readonly isUnfolded: boolean;
  // Told whether the clamped description hides lines: the card offers to unfold it then.
  readonly onOverflowChange: (isOverflowing: boolean) => void;
}

const CLAMPED_LINES = 3;

// The description of an entity on its card: its first lines, all of them once unfolded.
export const ClampedDescription = ({
  description,
  isUnfolded,
  onOverflowChange,
}: Readonly<Props>) => {
  const textRef = useRef<HTMLParagraphElement>(null);

  // The overflow is measured once rendered clamped: it depends on the card width.
  useLayoutEffect(() => {
    const text = textRef.current;

    if (!text || isUnfolded) {
      return undefined;
    }

    const measure = () => {
      onOverflowChange(text.scrollHeight > text.clientHeight + 1);
    };
    const observer = new ResizeObserver(measure);

    measure();
    observer.observe(text);

    return () => {
      observer.disconnect();
    };
  }, [description, isUnfolded, onOverflowChange]);

  if (!description) {
    return (
      <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
        Aucune description
      </Typography>
    );
  }

  return (
    <Typography
      ref={textRef}
      sx={{
        mt: 1,
        whiteSpace: 'pre-line',
        ...(isUnfolded
          ? {}
          : {
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: CLAMPED_LINES,
            }),
      }}
      variant="body2"
    >
      {description}
    </Typography>
  );
};
